/**
 * Creates a built-in flow's NoPain design mails, one request per mail.
 *
 * A Workers Free plan allows only 50 D1 queries and 50 subrequests per
 * request, and the Welcome flow alone has 18 mails (3 steps, 6 languages)
 * and 6 pictures. So the server creates ONE mail per call
 * (`POST /automations/:id/design-mail`) and the flow is saved once at the
 * end, with the ids of all the mails.
 */
import { apiFetch } from './api';
import type { FlowDefinition, FlowLanguageVariant } from '../types/api';

/** One mail of a built-in flow, as `designMails` / `mails` list them: English first. */
export interface DesignMailRef {
  language: string;
  stepId: string;
}

export interface DesignMails {
  /** Step id to its English (default) template. */
  templateIds: Record<string, string>;
  /** Step id to its language variants. */
  variants: Record<string, FlowLanguageVariant[]>;
  /** Mails that could not be created (a translation that failed is skipped). */
  failed: DesignMailRef[];
}

interface CreatedMail {
  pending?: false;
  stepId: string;
  language: string;
  templateId: string;
  subject: string | null;
  copies?: Record<string, string>;
}

/** A mail with more pictures than one request copies: ask again with these. */
interface PendingMail {
  pending: true;
  copies: Record<string, string>;
  skip: string[];
}

/** Rounds of picture copying one mail may take (a mail has at most a dozen pictures). */
const MAX_ROUNDS = 8;

async function createOne(
  automationId: string,
  mail: DesignMailRef,
  englishTemplateId: string | undefined,
  knownCopies: Record<string, string>,
): Promise<CreatedMail> {
  // `knownCopies`: pictures earlier mails of this flow already put on the media domain.
  let progress: { copies?: Record<string, string>; skip?: string[] } = { copies: knownCopies };
  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    const answer = await apiFetch<CreatedMail | PendingMail>(
      `/automations/${automationId}/design-mail`,
      {
        method: 'POST',
        body: {
          language: mail.language,
          stepId: mail.stepId,
          ...(englishTemplateId ? { englishTemplateId } : {}),
          ...progress,
        },
      },
    );
    if (!answer.pending) return answer;
    progress = { copies: answer.copies, skip: answer.skip };
  }
  throw new Error('The pictures of this mail could not all be copied.');
}

/**
 * Creates every mail in `plan`, in order. An English mail that fails stops
 * the run (the flow would have no mail for that step); a translation that
 * fails is skipped and listed in `failed`.
 */
export async function createDesignMails(
  automationId: string,
  plan: readonly DesignMailRef[],
  onProgress?: (done: number, total: number) => void,
): Promise<DesignMails> {
  const result: DesignMails = { templateIds: {}, variants: {}, failed: [] };
  let done = 0;
  let known: Record<string, string> = {};
  for (const mail of plan) {
    try {
      const created = await createOne(
        automationId,
        mail,
        mail.language !== 'en' ? result.templateIds[mail.stepId] : undefined,
        known,
      );
      known = created.copies ?? known;
      if (mail.language === 'en') {
        result.templateIds[mail.stepId] = created.templateId;
      } else {
        (result.variants[mail.stepId] ??= []).push({
          language: mail.language,
          templateId: created.templateId,
          subject: created.subject ?? '',
        });
      }
    } catch (err) {
      if (mail.language === 'en') throw err;
      result.failed.push(mail);
    }
    done += 1;
    onProgress?.(done, plan.length);
  }
  return result;
}

/** The flow with the mails put into its send steps (nothing is saved here). */
export function withDesignMails(definition: FlowDefinition, mails: DesignMails): FlowDefinition {
  return {
    ...definition,
    steps: definition.steps.map((step) => {
      if (step.type !== 'send_template') return step;
      const templateId = mails.templateIds[step.id];
      if (!templateId) return step;
      // The new mails replace the step's old language variants too, so a
      // step never mixes the new English mail with old translations.
      const { variants: _old, ...rest } = step;
      const variants = mails.variants[step.id];
      return { ...rest, templateId, ...(variants?.length ? { variants } : {}) };
    }),
  };
}

/** A short English note on the translations that could not be created, or null. */
export function missingMailsNote(failed: readonly DesignMailRef[]): string | null {
  if (failed.length === 0) return null;
  const languages = [...new Set(failed.map((m) => m.language.toUpperCase()))].join(', ');
  return `Some translations could not be created (${languages}); those contacts get the English mail. Use "Use the NoPain design mails" to try again.`;
}
